// Centralized media optimization with connection mode-aware caps
// This ensures NO component can accidentally request full-res images

import { useMemo } from 'react';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';

export type Mode = 'full' | 'balanced' | 'lite' | 'offline';

// Cloudinary cloud name from your setup
const CLOUDINARY_CLOUD = 'yldsocialvideos';

// Image optimization caps based on connection mode
const IMAGE_CAPS = {
  full: { w: 1200, q: 'auto' }, // Increased for retina support
  balanced: { w: 640, q: 'auto:eco' },
  lite: { w: 480, q: 'auto:low' }, // Slightly increased for better lite experience
  offline: { w: 320, q: 'auto:low' } // Minimal for offline
} as const;

// Video quality and resolution caps based on connection mode
const VIDEO_CAPS = {
  full: { bitrate: '2000k', height: 720, quality: 'auto' }, // 720p for full mode
  balanced: { bitrate: '1000k', height: 480, quality: 'auto:eco' }, // 480p for balanced
  lite: { bitrate: '500k', height: 360, quality: 'auto:low' }, // 360p for lite mode  
  offline: { bitrate: '300k', height: 240, quality: 'auto:low' } // 240p for offline
} as const;

/**
 * Generate responsive srcset for images with connection mode-aware optimization
 * @param id - Cloudinary public ID
 * @param mode - Connection mode
 * @param format - Image format (webp, avif)
 */
export function cldImageSrcSet(id: string, mode?: Mode, format: 'webp' | 'avif' = 'webp'): string {
  const currentMode = mode || detectCurrentMode();
  const caps = IMAGE_CAPS[currentMode];
  
  // Generate multiple sizes for srcset based on mode capabilities
  const sizes = currentMode === 'lite' 
    ? [320, 480] // Lite: smaller sizes only
    : currentMode === 'balanced'
    ? [320, 480, 640] // Balanced: medium range
    : [320, 480, 640, 960, 1200]; // Full: all sizes including retina
  
  return sizes
    .filter(size => size <= caps.w) // Respect mode caps
    .map(width => {
      const quality = currentMode === 'lite' ? 'auto:low' : 
                     currentMode === 'balanced' ? 'auto:eco' : 'auto';
      return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/f_${format},q_${quality},w_${width},c_limit,dpr_auto/${id} ${width}w`;
    })
    .join(', ');
}

/**
 * Generate LQIP (Low Quality Image Placeholder) for blur-up effect
 * @param id - Cloudinary public ID
 */
export function cldLQIP(id: string): string {
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/f_auto,q_auto:low,w_40,c_limit,e_blur:1000/${id}`;
}

/**
 * Get optimized image URL with modern format support and connection mode-aware caps
 * @param id - Cloudinary public ID
 * @param mode - Connection mode (if not provided, uses current mode)
 * @param maxWidth - Optional max width override (will be capped by mode limits)
 * @param format - Image format (auto, webp, avif)
 */
export function cldImage(id: string, mode?: Mode, maxWidth?: number, format: 'auto' | 'webp' | 'avif' = 'auto'): string {
  const currentMode = mode || detectCurrentMode();
  const caps = IMAGE_CAPS[currentMode];
  
  // Enforce caps - cannot exceed mode limits
  const width = maxWidth ? Math.min(maxWidth, caps.w) : caps.w;
  
  // Use modern formats for better compression
  const formatParam = format === 'auto' ? 'f_auto' : `f_${format}`;
  
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/${formatParam},q_${caps.q},w_${width},c_limit,dpr_auto/${id}`;
}

/**
 * Get optimized video poster image URL
 * @param id - Cloudinary public ID  
 * @param mode - Connection mode
 * @param w - Width (will be capped by mode)
 * @param h - Height (will be capped by mode)
 */
export function cldPoster(id: string, mode?: Mode, w = 480, h = 270): string {
  const currentMode = mode || detectCurrentMode();
  const caps = IMAGE_CAPS[currentMode];
  
  // Enforce caps - cannot exceed mode limits
  const width = Math.min(w, caps.w);
  const height = Math.min(h, Math.floor(caps.w * (h / w))); // Maintain aspect ratio
  
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/video/upload/so_2,c_fill,w_${width},h_${height},f_auto,q_auto:eco/${id}.jpg`;
}

/**
 * Get optimized video URL using public delivery model
 * @param id - Cloudinary public ID
 * @param mode - Connection mode (simplified to just use w_640 for lite)
 */
export function cldVideo(id: string, mode?: Mode): string {
  const currentMode = mode || detectCurrentMode();
  
  // Public delivery model: always MP4, codec auto, eco quality, 640px width
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/video/upload/f_mp4,vc_auto,q_auto:eco,w_640/${id}.mp4`;
}

/**
 * Helper to build a public (unsigned) 360p MP4 URL for tap/play resolution
 * @param cloud - Cloudinary cloud name
 * @param publicId - Cloudinary public ID (e.g., 'folder/video_name')
 */
export function cloudinaryLiteUrl(cloud: string, publicId: string): string {
  const t = 'f_mp4,vc_auto,q_auto:eco,w_640';
  return `https://res.cloudinary.com/${cloud}/video/upload/${t}/${publicId}.mp4`;
}

/**
 * Get HLS video URL for mobile/iOS (adaptive streaming)
 * @param id - Cloudinary public ID
 * @param mode - Connection mode
 */
export function cldVideoHLS(id: string, mode?: Mode): string {
  const currentMode = mode || detectCurrentMode();
  const caps = VIDEO_CAPS[currentMode];
  
  // HLS with adaptive bitrate streaming for mobile
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/video/upload/f_m3u8,vc_auto,q_${caps.quality},br_${caps.bitrate},h_${caps.height},c_limit/${id}.m3u8`;
}

/**
 * Get optimized avatar image URL (always small, for profile pics)
 * @param id - Cloudinary public ID
 * @param size - Size in pixels (capped at 150px max)
 */
export function cldAvatar(id: string, size = 100): string {
  // Avatars are always small and use eco quality
  const cappedSize = Math.min(size, 150); // Hard cap for avatars
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/f_auto,q_auto:eco,w_${cappedSize},h_${cappedSize},c_fill,g_face/${id}`;
}

/**
 * Get optimized thumbnail URL (always very small)
 * @param id - Cloudinary public ID
 */
export function cldThumbnail(id: string): string {
  // Thumbnails are always tiny with low quality
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/f_auto,q_auto:low,w_150,h_150,c_fill/${id}`;
}

/**
 * Extract Cloudinary public ID from a full URL
 * @param url - Full Cloudinary URL or public ID
 */
export function extractPublicId(url: string): string {
  if (!url) return '';
  
  // If it doesn't look like a URL, assume it's already a public ID
  if (!url.includes('http') && !url.includes('cloudinary.com')) {
    // Remove extension if present
    return url.replace(/\.(jpg|jpeg|png|gif|webp|mp4|webm|mov)$/i, '');
  }
  
  // Match Cloudinary URLs: /upload/[transformations]/[path/to/]publicId[.ext]
  // Handles both image and video URLs with optional transformations
  const cloudinaryPattern = /\/(?:image|video)\/upload\/(?:[^/]+\/)*(.+?)(?:\.[a-z0-9]+)?$/i;
  const match = url.match(cloudinaryPattern);
  
  if (match) {
    // Remove any extension from the captured group
    return match[1].replace(/\.(jpg|jpeg|png|gif|webp|mp4|webm|mov)$/i, '');
  }
  
  // Fallback: extract filename without extension
  const parts = url.split('/');
  const lastPart = parts[parts.length - 1];
  return lastPart.split('.')[0];
}

/**
 * Detect current connection mode from DOM or fallback
 */
function detectCurrentMode(): Mode {
  if (typeof window === 'undefined') return 'balanced';
  
  // Check DOM data attribute set by connection provider
  const mode = document.documentElement.dataset.mode as Mode;
  if (mode && ['full', 'balanced', 'lite', 'offline'].includes(mode)) {
    return mode;
  }
  
  // Fallback: check if lite class is present
  if (document.documentElement.classList.contains('lite')) {
    return 'lite';
  }
  
  // Default fallback
  return 'balanced';
}

/**
 * React hook to get connection mode-aware media functions
 * Memoized to prevent unnecessary re-renders of video components
 */
export function useMediaOptimization() {
  const mode = useConnectionMode();
  
  return useMemo(() => ({
    mode,
    image: (id: string, maxWidth?: number) => cldImage(id, mode, maxWidth),
    imageSrcSet: (id: string, format: 'webp' | 'avif' = 'webp') => cldImageSrcSet(id, mode, format), 
    lqip: (id: string) => cldLQIP(id),
    poster: (id: string, w?: number, h?: number) => cldPoster(id, mode, w, h),
    video: (id: string) => cldVideo(id, mode),
    videoHLS: (id: string) => cldVideoHLS(id, mode),
    avatar: (id: string, size?: number) => cldAvatar(id, size),
    thumbnail: (id: string) => cldThumbnail(id),
  }), [mode]);
}

// Validation functions to prevent raw URLs
export function validateImageUrl(url: string): boolean {
  if (!url.includes('res.cloudinary.com')) return true; // Allow non-Cloudinary URLs
  
  // Check if URL has optimization parameters
  const hasOptimization = /[?&](q_|w_|f_auto|c_|g_)/.test(url);
  
  if (!hasOptimization) {
    console.warn('⚠️ Raw Cloudinary URL detected! Use media.ts helpers instead:', url);
    return false;
  }
  
  return true;
}

// Development mode validation
if (import.meta.env.DEV) {
  // Override console.warn to catch raw Cloudinary URLs in development
  const originalWarn = console.warn;
  console.warn = (...args) => {
    const message = args.join(' ');
    if (message.includes('res.cloudinary.com') && !message.includes('q_')) {
      originalWarn('🚨 POSSIBLE RAW CLOUDINARY URL:', ...args);
      originalWarn('💡 Use media.ts helpers: cldImage(), cldVideo(), cldPoster()');
    } else {
      originalWarn(...args);
    }
  };

  // Check for untransformed images on first screen load
  const checkImageCaps = () => {
    const tooBig = [...document.images]
      .filter(img => img.currentSrc.includes('res.cloudinary.com'))
      .filter(img => (new URL(img.currentSrc)).search.includes('w_') === false); // crude check
    if (tooBig.length) {
      console.warn('[img-cap] Untransformed images found:', tooBig.map(i => i.currentSrc));
    }
  };

  // Run check after DOM is loaded and images have had time to load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(checkImageCaps, 1000); // Give images time to load
    });
  } else {
    setTimeout(checkImageCaps, 1000);
  }
}